import type {
  RealtimeBackend,
  RealtimeCommand,
  RealtimeConnectParams,
  RealtimeEvent,
  RealtimeGameAsteroid,
  RealtimeGameBullet,
  RealtimeGameDelta,
  RealtimeGameMine,
  RealtimeGameModeState,
  RealtimeGamePellet,
  RealtimeGamePlayer,
  RealtimeInputPayload,
} from "./realtime.types";
import { createStdbBindingsBridge } from "./stdb.bridge";

type LobbyStateEvent = Omit<
  Extract<RealtimeEvent, { type: "lobby_state" }>,
  "type"
>;
type LobbyListEvent = Omit<
  Extract<RealtimeEvent, { type: "lobby_list" }>,
  "type"
>;
type PartyStateEvent = Omit<
  Extract<RealtimeEvent, { type: "party_state" }>,
  "type"
>;
type GameStateEvent = Omit<
  Extract<RealtimeEvent, { type: "game_state" }>,
  "type"
>;

type LobbyRoster = Map<string, string>;
type KillCredit = {
  name: string;
  remaining: number;
};
const MOBA_NPC_XP_REWARD = 10;
const TEAMS_VICTORY_SCORE = 5000;

export interface StdbBridge {
  connect(config: {
    uri: string;
    database: string;
    token?: string;
  }): Promise<void>;
  disconnect(): Promise<void>;
  onConnected(handler: (event: { identity: string }) => void): () => void;
  onDisconnected?(handler: () => void): () => void;
  onConnectError?(handler: (error: Error) => void): () => void;
  onLobbyState(handler: (event: LobbyStateEvent) => void): () => void;
  onLobbyList(handler: (event: LobbyListEvent) => void): () => void;
  onPartyState(handler: (event: PartyStateEvent) => void): () => void;
  onGameState?(handler: (event: GameStateEvent) => void): () => void;
  requestLobbyList?(): Promise<void>;
  deployMine?(): Promise<void>;
  leaveLobby?(): Promise<void>;
  debugPelletBoost?(): Promise<void>;
  debugToggleInvincibility?(): Promise<void>;
  debugInstantDeath?(): Promise<void>;
  debugInstantVictory?(): Promise<void>;
  joinLobby(request: {
    lobbyId: string;
    playerName: string;
    primaryWeapon?: string;
  }): Promise<void>;
  setLoadout?(request: { primaryWeapon: string }): Promise<void>;
  partyCreate(request: { playerName: string }): Promise<void>;
  partyJoinCode(request: {
    joinCode: string;
    playerName: string;
  }): Promise<void>;
  partyToggleReady(): Promise<void>;
  partyLeave(): Promise<void>;
  partyQueue(): Promise<void>;
  submitInput(request: {
    lobbyId: string;
    sequence: number;
    input: RealtimeInputPayload;
  }): Promise<void>;
}

export interface StdbRealtimeBackendConfig {
  uri: string;
  database: string;
}

function toIdMap<T extends { id: string }>(rows: T[]): Map<string, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

function toLobbyRoster(players: LobbyStateEvent["players"]): LobbyRoster {
  return new Map(players.map((player) => [player.id, player.name]));
}

function toLobbyStateSignature(event: LobbyStateEvent): string {
  const playerSignature = [...event.players]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map(
      (player) =>
        `${player.id}|${player.name}|${player.color ?? ""}|${player.ready === true ? "1" : "0"}`,
    )
    .join(";");
  return [
    event.lobbyId,
    event.started ? "1" : "0",
    String(event.secondsUntilStart),
    event.teamMode ?? "",
    playerSignature,
  ].join("#");
}

function emitLobbyMembershipDelta(params: {
  previousRoster: LobbyRoster;
  nextPlayers: LobbyStateEvent["players"];
  emit: (event: RealtimeEvent) => void;
}): void {
  const nextRoster = toLobbyRoster(params.nextPlayers);

  for (const player of params.nextPlayers) {
    if (params.previousRoster.has(player.id)) {
      continue;
    }
    params.emit({
      type: "player_joined",
      playerId: player.id,
      playerName: player.name,
    });
  }

  for (const [playerId, playerName] of params.previousRoster) {
    if (nextRoster.has(playerId)) {
      continue;
    }
    params.emit({
      type: "player_left",
      playerId,
      playerName,
    });
  }
}

function vectorsEqual(
  left: { x: number; y: number },
  right: { x: number; y: number },
): boolean {
  return left.x === right.x && left.y === right.y;
}

function playersEqualForDelta(
  left: RealtimeGamePlayer,
  right: RealtimeGamePlayer,
): boolean {
  return (
    left.id === right.id &&
    left.name === right.name &&
    left.isBot === right.isBot &&
    left.color === right.color &&
    vectorsEqual(left.pos, right.pos) &&
    vectorsEqual(left.vel, right.vel) &&
    left.rotation === right.rotation &&
    left.targetRotation === right.targetRotation &&
    left.angularVelocity === right.angularVelocity &&
    left.health === right.health &&
    left.maxHealth === right.maxHealth &&
    left.kills === right.kills &&
    left.deaths === right.deaths &&
    left.pelletsCollected === right.pelletsCollected &&
    left.scale === right.scale &&
    left.damageMultiplier === right.damageMultiplier &&
    left.speedMultiplier === right.speedMultiplier &&
    left.lastAcknowledgedSequence === right.lastAcknowledgedSequence &&
    left.totalKills === right.totalKills &&
    left.totalDeaths === right.totalDeaths &&
    left.totalPelletsCollected === right.totalPelletsCollected &&
    left.captures === right.captures &&
    left.lastMineDeployAt === right.lastMineDeployAt &&
    left.lastBoostAt === right.lastBoostAt &&
    left.teamId === right.teamId &&
    left.primaryWeapon === right.primaryWeapon
  );
}

function bulletsEqualForDelta(
  left: RealtimeGameBullet,
  right: RealtimeGameBullet,
): boolean {
  return (
    left.id === right.id &&
    left.ownerId === right.ownerId &&
    vectorsEqual(left.pos, right.pos) &&
    vectorsEqual(left.vel, right.vel) &&
    left.age === right.age &&
    left.shooterScale === right.shooterScale &&
    left.damage === right.damage
  );
}

function asteroidsEqualForDelta(
  left: RealtimeGameAsteroid,
  right: RealtimeGameAsteroid,
): boolean {
  return (
    left.id === right.id &&
    vectorsEqual(left.pos, right.pos) &&
    vectorsEqual(left.vel, right.vel) &&
    left.sizeLevel === right.sizeLevel &&
    left.rotation === right.rotation &&
    left.rotationSpeed === right.rotationSpeed &&
    left.health === right.health &&
    left.maxHealth === right.maxHealth
  );
}

function pelletsEqualForDelta(
  left: RealtimeGamePellet,
  right: RealtimeGamePellet,
): boolean {
  return (
    left.id === right.id &&
    vectorsEqual(left.pos, right.pos) &&
    vectorsEqual(left.vel, right.vel) &&
    left.age === right.age &&
    left.value === right.value &&
    left.lifetime === right.lifetime
  );
}

function minesEqualForDelta(
  left: RealtimeGameMine,
  right: RealtimeGameMine,
): boolean {
  return (
    left.id === right.id &&
    left.ownerId === right.ownerId &&
    vectorsEqual(left.pos, right.pos) &&
    vectorsEqual(left.vel, right.vel) &&
    left.health === right.health &&
    left.maxHealth === right.maxHealth &&
    left.deployTime === right.deployTime &&
    left.radius === right.radius
  );
}

function createDeltaForRows<T extends { id: string }>(
  previousRows: T[],
  nextRows: T[],
  areEqual: (left: T, right: T) => boolean,
): { created: T[]; updated: T[]; deletedIds: string[] } {
  const previousById = toIdMap(previousRows);
  const nextById = toIdMap(nextRows);
  const created: T[] = [];
  const updated: T[] = [];
  const deletedIds: string[] = [];

  for (const nextRow of nextRows) {
    const previousRow = previousById.get(nextRow.id);
    if (!previousRow) {
      created.push(nextRow);
      continue;
    }
    if (!areEqual(previousRow, nextRow)) {
      updated.push(nextRow);
    }
  }

  for (const previousRow of previousRows) {
    if (!nextById.has(previousRow.id)) {
      deletedIds.push(previousRow.id);
    }
  }

  return { created, updated, deletedIds };
}

function deriveGameDelta(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): RealtimeGameDelta {
  const playerDelta = createDeltaForRows(
    params.previousGameState.players,
    params.nextGameState.players,
    playersEqualForDelta,
  );
  const bulletDelta = createDeltaForRows(
    params.previousGameState.bullets,
    params.nextGameState.bullets,
    bulletsEqualForDelta,
  );
  const asteroidDelta = createDeltaForRows(
    params.previousGameState.asteroids,
    params.nextGameState.asteroids,
    asteroidsEqualForDelta,
  );
  const pelletDelta = createDeltaForRows(
    params.previousGameState.pellets,
    params.nextGameState.pellets,
    pelletsEqualForDelta,
  );
  const mineDelta = createDeltaForRows(
    params.previousGameState.mines,
    params.nextGameState.mines,
    minesEqualForDelta,
  );

  return {
    timestamp: params.nextGameState.timestamp,
    sequence: params.nextGameState.sequence,
    createdPlayers: playerDelta.created,
    updatedPlayers: playerDelta.updated,
    deletedPlayerIds: playerDelta.deletedIds,
    createdBullets: bulletDelta.created,
    updatedBullets: bulletDelta.updated,
    deletedBulletIds: bulletDelta.deletedIds,
    createdAsteroids: asteroidDelta.created,
    updatedAsteroids: asteroidDelta.updated,
    deletedAsteroidIds: asteroidDelta.deletedIds,
    createdPellets: pelletDelta.created,
    updatedPellets: pelletDelta.updated,
    deletedPelletIds: pelletDelta.deletedIds,
    createdMines: mineDelta.created,
    updatedMines: mineDelta.updated,
    deletedMineIds: mineDelta.deletedIds,
    gameModeState: params.nextGameState.gameModeState,
    performance: params.nextGameState.performance,
  };
}

function isEntityDeltaEmpty(delta: RealtimeGameDelta): boolean {
  return (
    delta.createdPlayers.length === 0 &&
    delta.updatedPlayers.length === 0 &&
    delta.deletedPlayerIds.length === 0 &&
    delta.createdBullets.length === 0 &&
    delta.updatedBullets.length === 0 &&
    delta.deletedBulletIds.length === 0 &&
    delta.createdAsteroids.length === 0 &&
    delta.updatedAsteroids.length === 0 &&
    delta.deletedAsteroidIds.length === 0 &&
    delta.createdPellets.length === 0 &&
    delta.updatedPellets.length === 0 &&
    delta.deletedPelletIds.length === 0 &&
    delta.createdMines.length === 0 &&
    delta.updatedMines.length === 0 &&
    delta.deletedMineIds.length === 0
  );
}

function jsonStableEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function buildKillCredits(params: {
  previousPlayersById: Map<string, GameStateEvent["players"][number]>;
  nextPlayers: GameStateEvent["players"];
}): Map<string, KillCredit> {
  const credits = new Map<string, KillCredit>();

  for (const nextPlayer of params.nextPlayers) {
    const previousPlayer = params.previousPlayersById.get(nextPlayer.id);
    const previousKills = previousPlayer?.kills ?? 0;
    const killDelta = nextPlayer.kills - previousKills;

    if (killDelta <= 0) {
      continue;
    }

    credits.set(nextPlayer.id, {
      name: nextPlayer.name,
      remaining: killDelta,
    });
  }

  return credits;
}

function consumeKillCredit(
  killCredits: Map<string, KillCredit>,
  victimId: string,
): { id: string; name: string } | null {
  for (const [killerId, credit] of killCredits) {
    if (killerId === victimId || credit.remaining <= 0) {
      continue;
    }

    credit.remaining -= 1;
    if (credit.remaining <= 0) {
      killCredits.delete(killerId);
    }

    return { id: killerId, name: credit.name };
  }

  return null;
}

function derivePlayerKillEvents(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): Array<Extract<RealtimeEvent, { type: "player_killed" }>> {
  const previousPlayersById = toIdMap(params.previousGameState.players);
  const killCredits = buildKillCredits({
    previousPlayersById,
    nextPlayers: params.nextGameState.players,
  });

  const events: Array<Extract<RealtimeEvent, { type: "player_killed" }>> = [];

  for (const nextPlayer of params.nextGameState.players) {
    const previousPlayer = previousPlayersById.get(nextPlayer.id);
    const previousDeaths = previousPlayer?.deaths ?? 0;
    const deathDelta = nextPlayer.deaths - previousDeaths;

    if (deathDelta <= 0) {
      continue;
    }

    for (let index = 0; index < deathDelta; index += 1) {
      const killer = consumeKillCredit(killCredits, nextPlayer.id);
      events.push({
        type: "player_killed",
        killerId: killer?.id ?? "",
        victimId: nextPlayer.id,
        killerName: killer?.name ?? "Unknown",
        victimName: nextPlayer.name,
        deathX: previousPlayer?.pos.x ?? nextPlayer.pos.x,
        deathY: previousPlayer?.pos.y ?? nextPlayer.pos.y,
        deathVx: previousPlayer?.vel.x ?? nextPlayer.vel.x,
        deathVy: previousPlayer?.vel.y ?? nextPlayer.vel.y,
      });
    }
  }

  return events;
}

function inferDamageSource(
  playerX: number,
  playerY: number,
  previousMines: RealtimeGameMine[],
  nextMines: RealtimeGameMine[],
  asteroids: RealtimeGameAsteroid[],
  players: RealtimeGamePlayer[],
  damagedPlayerId: string,
): string {
  const nextMineIds = new Set(nextMines.map((m) => m.id));
  for (const mine of previousMines) {
    if (!nextMineIds.has(mine.id)) {
      const dx = playerX - mine.pos.x;
      const dy = playerY - mine.pos.y;
      if (dx * dx + dy * dy < 200 * 200) {
        return "mine";
      }
    }
  }

  for (const asteroid of asteroids) {
    const dx = playerX - asteroid.pos.x;
    const dy = playerY - asteroid.pos.y;
    const asteroidRadius = 15 + asteroid.sizeLevel * 10;
    const threshold = (10 + asteroidRadius) * 1.5;
    if (dx * dx + dy * dy < threshold * threshold) {
      return "asteroid";
    }
  }

  for (const otherPlayer of players) {
    if (otherPlayer.id === damagedPlayerId) continue;
    const dx = playerX - otherPlayer.pos.x;
    const dy = playerY - otherPlayer.pos.y;
    if (dx * dx + dy * dy < 30 * 30) {
      return "collision";
    }
  }

  return "bullet";
}

function deriveDamageBatchEvent(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): Extract<RealtimeEvent, { type: "damage_batch" }> | null {
  const previousPlayersById = toIdMap(params.previousGameState.players);
  const events: Array<{
    x: number;
    y: number;
    damage: number;
    source: string;
  }> = [];

  for (const nextPlayer of params.nextGameState.players) {
    const previousPlayer = previousPlayersById.get(nextPlayer.id);
    if (!previousPlayer) {
      continue;
    }

    const healthDelta = previousPlayer.health - nextPlayer.health;
    const roundedDamage = Math.round(healthDelta);
    if (roundedDamage <= 0) {
      continue;
    }

    events.push({
      x: nextPlayer.pos.x,
      y: nextPlayer.pos.y,
      damage: roundedDamage,
      source: inferDamageSource(
        nextPlayer.pos.x,
        nextPlayer.pos.y,
        params.previousGameState.mines,
        params.nextGameState.mines,
        params.nextGameState.asteroids,
        params.nextGameState.players,
        nextPlayer.id,
      ),
    });
  }

  if (events.length === 0) {
    return null;
  }

  return {
    type: "damage_batch",
    events,
  };
}

function deriveMineLifecycleEvents(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): RealtimeEvent[] {
  const previousMinesById = toIdMap(params.previousGameState.mines);
  const nextMinesById = toIdMap(params.nextGameState.mines);
  const events: RealtimeEvent[] = [];

  for (const nextMine of params.nextGameState.mines) {
    if (previousMinesById.has(nextMine.id)) {
      continue;
    }
    events.push({
      type: "mine_deployed",
      mine: nextMine,
    });
  }

  for (const previousMine of params.previousGameState.mines) {
    if (nextMinesById.has(previousMine.id)) {
      continue;
    }
    events.push({
      type: "mine_exploded",
      x: previousMine.pos.x,
      y: previousMine.pos.y,
      radius: previousMine.radius,
    });
  }

  return events;
}

function deriveAsteroidDestroyedEvents(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): Array<Extract<RealtimeEvent, { type: "asteroid_destroyed" }>> {
  const nextAsteroidsById = toIdMap(params.nextGameState.asteroids);
  const events: Array<Extract<RealtimeEvent, { type: "asteroid_destroyed" }>> =
    [];

  for (const previousAsteroid of params.previousGameState.asteroids) {
    if (nextAsteroidsById.has(previousAsteroid.id)) {
      continue;
    }

    events.push({
      type: "asteroid_destroyed",
      x: previousAsteroid.pos.x,
      y: previousAsteroid.pos.y,
      sizeLevel: previousAsteroid.sizeLevel,
      vx: previousAsteroid.vel.x,
      vy: previousAsteroid.vel.y,
    });
  }

  return events;
}

function deriveFallbackCollisionDeaths(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
  killVictimIds: Set<string>;
}): RealtimeEvent[] {
  const previousPlayersById = toIdMap(params.previousGameState.players);
  const nextMinesById = toIdMap(params.nextGameState.mines);
  const mineExplosions = params.previousGameState.mines.filter(
    (mine) => !nextMinesById.has(mine.id),
  );
  const events: RealtimeEvent[] = [];

  for (const nextPlayer of params.nextGameState.players) {
    const previousPlayer = previousPlayersById.get(nextPlayer.id);
    if (!previousPlayer) {
      continue;
    }

    const deathDelta = nextPlayer.deaths - previousPlayer.deaths;
    if (deathDelta <= 0) {
      continue;
    }

    if (params.killVictimIds.has(nextPlayer.id)) {
      continue;
    }

    for (let deathIndex = 0; deathIndex < deathDelta; deathIndex += 1) {
      const mineExplosion = mineExplosions.find((mine) => {
        const dx = mine.pos.x - previousPlayer.pos.x;
        const dy = mine.pos.y - previousPlayer.pos.y;
        const radius = (mine.radius || 14) + 120;
        return dx * dx + dy * dy <= radius * radius;
      });
      if (mineExplosion) {
        const killer = mineExplosion.ownerId
          ? previousPlayersById.get(mineExplosion.ownerId)
          : null;
        events.push({
          type: "player_mine_death",
          playerId: nextPlayer.id,
          playerName: nextPlayer.name,
          killerId: killer?.id,
          killerName: killer?.name,
        });
        continue;
      }

      const asteroidCollision = params.previousGameState.asteroids.find(
        (asteroid) => {
          const dx = asteroid.pos.x - previousPlayer.pos.x;
          const dy = asteroid.pos.y - previousPlayer.pos.y;
          const radius = 60 + Math.max(16, previousPlayer.scale * 22);
          return dx * dx + dy * dy <= radius * radius;
        },
      );
      if (asteroidCollision) {
        const speed = Math.round(
          Math.hypot(previousPlayer.vel.x, previousPlayer.vel.y),
        );
        events.push({
          type: "player_crashed_asteroid",
          playerId: nextPlayer.id,
          playerName: nextPlayer.name,
        });
        events.push({
          type: "player_crashed",
          playerId: nextPlayer.id,
          playerName: nextPlayer.name,
          speed,
        });
        continue;
      }

      const collisionPeer = params.previousGameState.players.find((player) => {
        if (player.id === previousPlayer.id || player.health <= 0) {
          return false;
        }
        const dx = player.pos.x - previousPlayer.pos.x;
        const dy = player.pos.y - previousPlayer.pos.y;
        const radius =
          Math.max(16, player.scale * 22) +
          Math.max(16, previousPlayer.scale * 22);
        return dx * dx + dy * dy <= radius * radius;
      });
      if (collisionPeer) {
        events.push({
          type: "player_collision_death",
          playerId: nextPlayer.id,
          playerName: nextPlayer.name,
          killerId: collisionPeer.id,
          killerName: collisionPeer.name,
        });
      }
    }
  }

  return events;
}

function derivePlayerRemovalEvents(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
  localPlayerId: string | null;
}): RealtimeEvent[] {
  const nextPlayersById = toIdMap(params.nextGameState.players);
  const aliveRemaining = params.nextGameState.players.filter(
    (player) => player.health > 0,
  ).length;
  const events: RealtimeEvent[] = [];

  for (const previousPlayer of params.previousGameState.players) {
    if (nextPlayersById.has(previousPlayer.id)) {
      continue;
    }

    if (previousPlayer.health > 0) {
      continue;
    }

    events.push({
      type: "player_eliminated",
      playerId: previousPlayer.id,
      playerName: previousPlayer.name,
      remainingPlayers: aliveRemaining,
    });

    if (params.localPlayerId === previousPlayer.id) {
      events.push({
        type: "eliminated",
        reason: "You were eliminated!",
      });
    }
  }

  return events;
}

// FFA game-over is NOT derived client-side. The server uses lobby.gameOver for
// authoritative victory conditions (teams/CTF/MOBA). In FFA with bot respawning,
// transient "only 1 alive" states are normal and must not trigger game-over.
function deriveGameOverEvent(_params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): Extract<RealtimeEvent, { type: "game_over" }> | null {
  return null;
}

function getWinningTeams(
  teamScores: Record<number, number>,
  threshold: number,
): number[] {
  const winners: number[] = [];
  for (const key of Object.keys(teamScores)) {
    const teamId = Number.parseInt(key, 10);
    if (Number.isNaN(teamId)) {
      continue;
    }
    if ((teamScores[teamId] ?? 0) >= threshold) {
      winners.push(teamId);
    }
  }
  winners.sort((left, right) => left - right);
  return winners;
}

function buildGameEndPlayerStats(params: {
  players: GameStateEvent["players"];
  teamScores: Record<number, number>;
}): Extract<RealtimeEvent, { type: "game_end" }>["playerStats"] {
  return params.players.map((player) => {
    const teamId =
      typeof player.teamId === "number" ? player.teamId : undefined;
    const score =
      typeof teamId === "number"
        ? (params.teamScores[teamId] ?? 0)
        : player.pelletsCollected;
    return {
      playerId: player.id,
      playerName: player.name,
      kills: player.kills,
      deaths: player.deaths,
      pelletsCollected: player.pelletsCollected,
      score,
      ...(typeof teamId === "number" ? { teamId } : {}),
    };
  });
}

function deriveGameEndEvent(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
}): Extract<RealtimeEvent, { type: "game_end" }> | null {
  const nextModeState = params.nextGameState.gameModeState;
  if (!nextModeState) {
    return null;
  }

  const playerStats = buildGameEndPlayerStats({
    players: params.nextGameState.players,
    teamScores: nextModeState.teamScores,
  });

  if (nextModeState.mode === "moba") {
    const nextMobaState = params.nextGameState.mobaState;
    if (!nextMobaState) {
      return null;
    }

    const destroyedBases = nextMobaState.bases.filter(
      (base) => base.health <= 0,
    );
    if (destroyedBases.length === 0) {
      return null;
    }

    const previousDestroyedCount =
      params.previousGameState.mobaState?.bases.filter(
        (base) => base.health <= 0,
      ).length ?? 0;
    if (previousDestroyedCount > 0) {
      return null;
    }

    if (destroyedBases.length > 1) {
      return {
        type: "game_end",
        winner: -1,
        isDraw: true,
        reason: "Draw - both bases destroyed simultaneously!",
        finalScores: nextModeState.teamScores,
        playerStats,
      };
    }

    const destroyedBase = destroyedBases[0];
    if (!destroyedBase) {
      return null;
    }
    const winnerTeamId = destroyedBase.teamId === 0 ? 1 : 0;
    return {
      type: "game_end",
      winner: winnerTeamId,
      isDraw: false,
      reason: `Team ${winnerTeamId} wins by destroying the enemy base!`,
      finalScores: nextModeState.teamScores,
      playerStats,
    };
  }

  const isTeamsMode = nextModeState.mode === "teams";
  const isCtfMode = nextModeState.mode === "ctf";
  if (!isTeamsMode && !isCtfMode) {
    return null;
  }

  const threshold = isTeamsMode
    ? TEAMS_VICTORY_SCORE
    : (nextModeState.captureLimit ?? 3);
  const winners = getWinningTeams(nextModeState.teamScores, threshold);
  if (winners.length === 0) {
    return null;
  }

  const previousModeState = params.previousGameState.gameModeState;
  if (previousModeState?.mode === nextModeState.mode) {
    const previousWinners = getWinningTeams(
      previousModeState.teamScores,
      threshold,
    );
    if (previousWinners.length > 0) {
      return null;
    }
  }

  if (winners.length > 1) {
    return {
      type: "game_end",
      winner: -1,
      isDraw: true,
      reason: isTeamsMode
        ? "Draw - multiple teams reached victory threshold."
        : "Draw - multiple teams reached capture limit.",
      finalScores: nextModeState.teamScores,
      playerStats,
    };
  }

  const winnerTeamId = winners[0];
  if (winnerTeamId === undefined) {
    return null;
  }
  const winnerScore = nextModeState.teamScores[winnerTeamId] ?? threshold;
  return {
    type: "game_end",
    winner: winnerTeamId,
    isDraw: false,
    reason: isTeamsMode
      ? `Team ${winnerTeamId} wins with ${winnerScore} points!`
      : `Team ${winnerTeamId} wins with ${winnerScore} captures!`,
    finalScores: nextModeState.teamScores,
    playerStats,
  };
}

function areTeamScoresEqual(
  left: Record<number, number>,
  right: Record<number, number>,
): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    const numericKey = Number.parseInt(key, 10);
    if ((left[numericKey] ?? 0) !== (right[numericKey] ?? 0)) {
      return false;
    }
  }
  return true;
}

function areFlagsEqual(
  left: RealtimeGameModeState["flags"],
  right: RealtimeGameModeState["flags"],
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const leftById = new Map(left.map((flag) => [flag.id, flag]));
  for (const rightFlag of right) {
    const leftFlag = leftById.get(rightFlag.id);
    if (!leftFlag) {
      return false;
    }

    if (
      leftFlag.teamId !== rightFlag.teamId ||
      leftFlag.state !== rightFlag.state ||
      leftFlag.carrierId !== rightFlag.carrierId ||
      leftFlag.droppedAt !== rightFlag.droppedAt
    ) {
      return false;
    }

    const leftPosX = leftFlag.pos?.x;
    const leftPosY = leftFlag.pos?.y;
    const rightPosX = rightFlag.pos?.x;
    const rightPosY = rightFlag.pos?.y;
    if (leftPosX !== rightPosX || leftPosY !== rightPosY) {
      return false;
    }
  }

  return true;
}

function areMobaBasesEqual(
  left: NonNullable<GameStateEvent["mobaState"]>["bases"],
  right: NonNullable<GameStateEvent["mobaState"]>["bases"],
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const leftById = new Map(left.map((base) => [base.id, base]));
  for (const rightBase of right) {
    const leftBase = leftById.get(rightBase.id);
    if (!leftBase) {
      return false;
    }

    if (
      leftBase.teamId !== rightBase.teamId ||
      leftBase.health !== rightBase.health ||
      leftBase.maxHealth !== rightBase.maxHealth ||
      leftBase.radius !== rightBase.radius ||
      leftBase.pos.x !== rightBase.pos.x ||
      leftBase.pos.y !== rightBase.pos.y
    ) {
      return false;
    }
  }

  return true;
}

function areMobaNpcsEqual(
  left: NonNullable<GameStateEvent["mobaState"]>["npcs"],
  right: NonNullable<GameStateEvent["mobaState"]>["npcs"],
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const leftById = new Map(left.map((npc) => [npc.id, npc]));
  for (const rightNpc of right) {
    const leftNpc = leftById.get(rightNpc.id);
    if (!leftNpc) {
      return false;
    }

    if (
      leftNpc.teamId !== rightNpc.teamId ||
      leftNpc.laneId !== rightNpc.laneId ||
      leftNpc.health !== rightNpc.health ||
      leftNpc.maxHealth !== rightNpc.maxHealth ||
      leftNpc.radius !== rightNpc.radius ||
      leftNpc.pos.x !== rightNpc.pos.x ||
      leftNpc.pos.y !== rightNpc.pos.y ||
      leftNpc.vel.x !== rightNpc.vel.x ||
      leftNpc.vel.y !== rightNpc.vel.y
    ) {
      return false;
    }
  }

  return true;
}

function deriveFlagsUpdateEvent(params: {
  previousGameState: GameStateEvent | null;
  nextGameState: GameStateEvent;
}): Extract<RealtimeEvent, { type: "flags_update" }> | null {
  const nextModeState = params.nextGameState.gameModeState;
  if (!nextModeState) {
    return null;
  }

  const previousModeState = params.previousGameState?.gameModeState;
  if (
    previousModeState &&
    previousModeState.mode === nextModeState.mode &&
    previousModeState.captureLimit === nextModeState.captureLimit &&
    areTeamScoresEqual(
      previousModeState.teamScores,
      nextModeState.teamScores,
    ) &&
    areFlagsEqual(previousModeState.flags, nextModeState.flags)
  ) {
    return null;
  }

  return {
    type: "flags_update",
    flags: nextModeState.flags,
    teamScores: nextModeState.teamScores,
    captureLimit: nextModeState.captureLimit ?? 0,
  };
}

function deriveMobaStateEvent(params: {
  previousGameState: GameStateEvent | null;
  nextGameState: GameStateEvent;
}): Extract<RealtimeEvent, { type: "moba_state" }> | null {
  const nextModeState = params.nextGameState.gameModeState;
  const nextMobaState = params.nextGameState.mobaState;
  if (!nextMobaState || nextModeState?.mode !== "moba") {
    return null;
  }

  const previousMobaState = params.previousGameState?.mobaState;
  if (
    previousMobaState &&
    previousMobaState.waveNumber === nextMobaState.waveNumber &&
    areTeamScoresEqual(
      previousMobaState.teamScores,
      nextMobaState.teamScores,
    ) &&
    areMobaBasesEqual(previousMobaState.bases, nextMobaState.bases) &&
    areMobaNpcsEqual(previousMobaState.npcs, nextMobaState.npcs)
  ) {
    return null;
  }

  return {
    type: "moba_state",
    bases: nextMobaState.bases,
    npcs: nextMobaState.npcs,
    teamScores: nextMobaState.teamScores,
    waveNumber: nextMobaState.waveNumber,
  };
}

function deriveMobaTransitionEvents(params: {
  previousGameState: GameStateEvent | null;
  nextGameState: GameStateEvent;
}): RealtimeEvent[] {
  const previousMobaState = params.previousGameState?.mobaState;
  const nextMobaState = params.nextGameState.mobaState;
  if (!previousMobaState || !nextMobaState) {
    return [];
  }

  const events: RealtimeEvent[] = [];
  const previousBasesById = new Map(
    previousMobaState.bases.map((base) => [base.id, base]),
  );
  const nextNpcsById = new Map(nextMobaState.npcs.map((npc) => [npc.id, npc]));

  for (const nextBase of nextMobaState.bases) {
    const previousBase = previousBasesById.get(nextBase.id);
    if (!previousBase) {
      continue;
    }
    const damage = previousBase.health - nextBase.health;
    if (damage > 0) {
      events.push({
        type: "base_damaged",
        baseTeamId: nextBase.teamId,
        damage: Math.round(damage),
        newHealth: nextBase.health,
        attackerId: null,
        attackerName: null,
      });
    }
    if (previousBase.health > 0 && nextBase.health <= 0) {
      events.push({
        type: "base_destroyed",
        baseTeamId: nextBase.teamId,
        destroyedBy: null,
        destroyedByName: null,
      });
    }
  }

  for (const previousNpc of previousMobaState.npcs) {
    if (nextNpcsById.has(previousNpc.id)) {
      continue;
    }
    events.push({
      type: "npc_killed",
      npcId: previousNpc.id,
      killerId: null,
      killerName: null,
      xpAwarded: MOBA_NPC_XP_REWARD,
    });
  }

  if (nextMobaState.waveNumber > previousMobaState.waveNumber) {
    events.push({
      type: "wave_spawned",
      waveNumber: nextMobaState.waveNumber,
      npcsSpawned: Math.max(
        0,
        nextMobaState.npcs.length - previousMobaState.npcs.length,
      ),
    });
  }

  return events;
}

function findScoreIncrease(params: {
  previousScores: Record<number, number>;
  nextScores: Record<number, number>;
}): { teamId: number; newScore: number } | null {
  const keys = new Set([
    ...Object.keys(params.previousScores),
    ...Object.keys(params.nextScores),
  ]);
  for (const key of keys) {
    const teamId = Number.parseInt(key, 10);
    const previousScore = params.previousScores[teamId] ?? 0;
    const nextScore = params.nextScores[teamId] ?? 0;
    if (nextScore > previousScore) {
      return {
        teamId,
        newScore: nextScore,
      };
    }
  }
  return null;
}

function buildPlayerNamesById(
  players: GameStateEvent["players"],
): Map<string, string> {
  return new Map(players.map((player) => [player.id, player.name]));
}

function deriveFlagTransitionEvents(params: {
  previousGameState: GameStateEvent | null;
  nextGameState: GameStateEvent;
}): RealtimeEvent[] {
  const previousModeState = params.previousGameState?.gameModeState;
  const nextModeState = params.nextGameState.gameModeState;
  if (!previousModeState || !nextModeState) {
    return [];
  }

  const previousFlagsById = new Map(
    previousModeState.flags.map((flag) => [flag.id, flag]),
  );
  const playerNamesById = buildPlayerNamesById(params.nextGameState.players);
  const scoreIncrease = findScoreIncrease({
    previousScores: previousModeState.teamScores,
    nextScores: nextModeState.teamScores,
  });
  const events: RealtimeEvent[] = [];

  for (const nextFlag of nextModeState.flags) {
    const previousFlag = previousFlagsById.get(nextFlag.id);
    if (!previousFlag || previousFlag.state === nextFlag.state) {
      continue;
    }

    if (nextFlag.state === "carried" && nextFlag.carrierId) {
      const playerId = nextFlag.carrierId;
      events.push({
        type: "flag_taken",
        teamId: nextFlag.teamId,
        playerId,
        playerName: playerNamesById.get(playerId) ?? "Unknown",
      });
      continue;
    }

    if (nextFlag.state === "dropped") {
      const playerId = previousFlag.carrierId ?? "";
      events.push({
        type: "flag_dropped",
        teamId: nextFlag.teamId,
        playerId,
        playerName: playerNamesById.get(playerId) ?? "Unknown",
        x: nextFlag.pos?.x ?? 0,
        y: nextFlag.pos?.y ?? 0,
      });
      continue;
    }

    if (nextFlag.state === "home" && previousFlag.state === "dropped") {
      const playerId = nextFlag.carrierId ?? "";
      events.push({
        type: "flag_returned",
        teamId: nextFlag.teamId,
        playerId,
        playerName: playerNamesById.get(playerId) ?? "Unknown",
      });
      continue;
    }

    if (
      nextFlag.state === "home" &&
      previousFlag.state === "carried" &&
      scoreIncrease
    ) {
      const playerId = previousFlag.carrierId ?? "";
      events.push({
        type: "flag_captured",
        flagTeamId: nextFlag.teamId,
        capturingTeamId: scoreIncrease.teamId,
        playerId,
        playerName: playerNamesById.get(playerId) ?? "Unknown",
        newScore: scoreIncrease.newScore,
      });
    }
  }

  return events;
}

function emitDerivedGameEvents(params: {
  previousGameState: GameStateEvent;
  nextGameState: GameStateEvent;
  localPlayerId: string | null;
  emit: (event: RealtimeEvent) => void;
}): void {
  const mineEvents = deriveMineLifecycleEvents(params);
  for (const event of mineEvents) {
    params.emit(event);
  }

  const asteroidEvents = deriveAsteroidDestroyedEvents(params);
  for (const event of asteroidEvents) {
    params.emit(event);
  }

  const damageBatch = deriveDamageBatchEvent(params);
  if (damageBatch) {
    params.emit(damageBatch);
    for (const event of damageBatch.events) {
      params.emit({
        type: "damage_dealt",
        x: event.x,
        y: event.y,
        damage: event.damage,
        source: event.source,
      });
    }
  }

  const killEvents = derivePlayerKillEvents(params);
  for (const event of killEvents) {
    params.emit(event);
  }

  const killVictimIds = new Set(killEvents.map((event) => event.victimId));
  const fallbackDeathEvents = deriveFallbackCollisionDeaths({
    previousGameState: params.previousGameState,
    nextGameState: params.nextGameState,
    killVictimIds,
  });
  for (const event of fallbackDeathEvents) {
    params.emit(event);
  }

  const removalEvents = derivePlayerRemovalEvents({
    previousGameState: params.previousGameState,
    nextGameState: params.nextGameState,
    localPlayerId: params.localPlayerId,
  });
  for (const event of removalEvents) {
    params.emit(event);
  }

  const gameEndEvent = deriveGameEndEvent({
    previousGameState: params.previousGameState,
    nextGameState: params.nextGameState,
  });
  if (gameEndEvent) {
    params.emit(gameEndEvent);
  }

  const gameOverEvent = deriveGameOverEvent({
    previousGameState: params.previousGameState,
    nextGameState: params.nextGameState,
  });
  if (gameOverEvent) {
    params.emit(gameOverEvent);
  }

  const flagEvents = deriveFlagTransitionEvents({
    previousGameState: params.previousGameState,
    nextGameState: params.nextGameState,
  });
  for (const event of flagEvents) {
    params.emit(event);
  }
}

function createRealtimeCommandSender(params: {
  bridge: StdbBridge;
  ensureConnected: () => Promise<void>;
  getCurrentPlayerId: () => string | null;
  emit: (event: RealtimeEvent) => void;
  resetDerivedState: () => void;
}): (command: RealtimeCommand) => Promise<void> {
  return async (command: RealtimeCommand): Promise<void> => {
    await params.ensureConnected();

    switch (command.type) {
      case "debug_pellet_boost":
        if (params.bridge.debugPelletBoost) {
          await params.bridge.debugPelletBoost();
        }
        return;
      case "debug_toggle_invincibility":
        if (params.bridge.debugToggleInvincibility) {
          await params.bridge.debugToggleInvincibility();
        }
        return;
      case "debug_instant_death":
        if (params.bridge.debugInstantDeath) {
          await params.bridge.debugInstantDeath();
        }
        return;
      case "debug_instant_victory":
        if (params.bridge.debugInstantVictory) {
          await params.bridge.debugInstantVictory();
        }
        return;
      case "leave_lobby":
        if (params.bridge.leaveLobby) {
          await params.bridge.leaveLobby();
          params.resetDerivedState();
          params.emit({ type: "left_lobby" });
        }
        return;
      case "deploy_mine":
        if (params.bridge.deployMine) {
          await params.bridge.deployMine();
        }
        return;
      case "join_lobby":
        await params.bridge.joinLobby({
          lobbyId: command.lobbyId,
          playerName: command.playerName,
          ...(typeof command.primaryWeapon === "string"
            ? { primaryWeapon: command.primaryWeapon }
            : {}),
        });
        return;
      case "set_loadout":
        if (params.bridge.setLoadout) {
          await params.bridge.setLoadout({
            primaryWeapon: command.primaryWeapon,
          });
        }
        return;
      case "request_lobby_list":
        if (params.bridge.requestLobbyList) {
          await params.bridge.requestLobbyList();
        }
        return;
      case "ping":
        params.emit({
          type: "pong",
          now: command.timestamp,
        });
        return;
      case "enable_autoplay":
        params.emit({
          type: "autoplay_enabled",
          playerId: params.getCurrentPlayerId() ?? "",
        });
        return;
      case "disable_autoplay":
        params.emit({
          type: "autoplay_disabled",
          playerId: params.getCurrentPlayerId() ?? "",
        });
        return;
      case "party_create":
        await params.bridge.partyCreate({ playerName: command.playerName });
        return;
      case "party_join_code":
        await params.bridge.partyJoinCode({
          joinCode: command.joinCode,
          playerName: command.playerName,
        });
        return;
      case "party_toggle_ready":
        await params.bridge.partyToggleReady();
        return;
      case "party_leave":
        await params.bridge.partyLeave();
        return;
      case "party_queue":
        await params.bridge.partyQueue();
        return;
      case "party_invite_steam":
      case "party_accept_invite":
      case "get_steam_friends":
        params.emit({
          type: "error",
          message:
            "Steam party integration is not available in this runtime yet.",
        });
        return;
      case "submit_input":
        await params.bridge.submitInput({
          lobbyId: command.lobbyId,
          sequence: command.sequence,
          input: command.input,
        });
        return;
    }
  };
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Centralized backend lifecycle and event wiring.
export function createStdbRealtimeBackend(
  bridge: StdbBridge,
  config: StdbRealtimeBackendConfig,
): RealtimeBackend {
  const listeners = new Set<(event: RealtimeEvent) => void>();
  let unsubscribers: Array<() => void> = [];
  let connected = false;
  let connectPromise: Promise<void> | null = null;
  let authToken: string | undefined;
  let currentPlayerId: string | null = null;
  let previousLobbyId: string | null = null;
  let previousLobbyRoster: LobbyRoster | null = null;
  let previousLobbyStateSignature: string | null = null;
  let previousGameState: GameStateEvent | null = null;

  const resetDerivedState = (): void => {
    previousLobbyId = null;
    previousLobbyRoster = null;
    previousLobbyStateSignature = null;
    previousGameState = null;
  };

  const emit = (event: RealtimeEvent): void => {
    for (const listener of listeners) {
      listener(event);
    }
  };

  const ensureBridgeSubscriptions = (): void => {
    if (unsubscribers.length > 0) {
      return;
    }

    unsubscribers = [
      bridge.onConnected((event) => {
        connected = true;
        currentPlayerId = event.identity;
        emit({ type: "connection_state", state: "connected" });
        emit({ type: "connected", identity: event.identity });
      }),
      ...(bridge.onDisconnected
        ? [
            bridge.onDisconnected(() => {
              connected = false;
              currentPlayerId = null;
              resetDerivedState();
              emit({
                type: "connection_state",
                state: "disconnected",
              });
            }),
          ]
        : []),
      ...(bridge.onConnectError
        ? [
            bridge.onConnectError((error) => {
              connected = false;
              currentPlayerId = null;
              resetDerivedState();
              emit({
                type: "connection_state",
                state: "error",
                errorMessage: error.message,
              });
              emit({ type: "error", message: error.message });
            }),
          ]
        : []),
      bridge.onLobbyList((event) => {
        emit({ type: "lobby_list", ...event });
      }),
      bridge.onPartyState((event) => {
        emit({ type: "party_state", ...event });
      }),
      ...(bridge.onGameState
        ? [
            bridge.onGameState((event) => {
              if (previousGameState) {
                const delta = deriveGameDelta({
                  previousGameState,
                  nextGameState: event,
                });
                const unchangedModeState = jsonStableEqual(
                  previousGameState.gameModeState,
                  event.gameModeState,
                );
                const unchangedMobaState = jsonStableEqual(
                  previousGameState.mobaState,
                  event.mobaState,
                );
                const unchangedPerformance = jsonStableEqual(
                  previousGameState.performance,
                  event.performance,
                );
                const hasNoMeaningfulChanges =
                  isEntityDeltaEmpty(delta) &&
                  unchangedModeState &&
                  unchangedMobaState &&
                  unchangedPerformance;
                if (hasNoMeaningfulChanges) {
                  return;
                }

                emitDerivedGameEvents({
                  previousGameState,
                  nextGameState: event,
                  localPlayerId: currentPlayerId,
                  emit,
                });

                emit({
                  type: "game_delta",
                  delta,
                });
              } else {
                emit({ type: "game_state", ...event });
              }

              const flagsUpdate = deriveFlagsUpdateEvent({
                previousGameState,
                nextGameState: event,
              });
              if (flagsUpdate) {
                emit(flagsUpdate);
              }

              const mobaStateEvent = deriveMobaStateEvent({
                previousGameState,
                nextGameState: event,
              });
              if (mobaStateEvent) {
                emit(mobaStateEvent);
              }

              const mobaTransitionEvents = deriveMobaTransitionEvents({
                previousGameState,
                nextGameState: event,
              });
              for (const mobaEvent of mobaTransitionEvents) {
                emit(mobaEvent);
              }

              previousGameState = event;
            }),
          ]
        : []),
      bridge.onLobbyState((event) => {
        const nextLobbyStateSignature = toLobbyStateSignature(event);
        if (
          previousLobbyId === event.lobbyId &&
          previousLobbyStateSignature === nextLobbyStateSignature
        ) {
          return;
        }

        const previousRosterForLobby =
          previousLobbyId === event.lobbyId ? previousLobbyRoster : null;

        emit({ type: "lobby_state", ...event });
        emit({ type: "player_count", count: event.players.length });

        if (previousRosterForLobby) {
          emitLobbyMembershipDelta({
            previousRoster: previousRosterForLobby,
            nextPlayers: event.players,
            emit,
          });
        }

        if (previousLobbyId !== event.lobbyId) {
          previousGameState = null;
        }

        previousLobbyId = event.lobbyId;
        previousLobbyRoster = toLobbyRoster(event.players);
        previousLobbyStateSignature = nextLobbyStateSignature;
      }),
    ];
  };

  const ensureConnected = async (): Promise<void> => {
    ensureBridgeSubscriptions();
    if (connected) {
      return;
    }
    if (connectPromise) {
      await connectPromise;
      return;
    }

    connectPromise = bridge
      .connect({
        uri: config.uri,
        database: config.database,
        token: authToken,
      })
      .then(() => {
        connected = true;
      })
      .finally(() => {
        connectPromise = null;
      });

    await connectPromise;
  };

  const sendCommand = createRealtimeCommandSender({
    bridge,
    ensureConnected,
    getCurrentPlayerId: () => currentPlayerId,
    emit,
    resetDerivedState,
  });

  return {
    async connect(params?: RealtimeConnectParams): Promise<void> {
      authToken = params?.token;
      await ensureConnected();
    },

    async disconnect(): Promise<void> {
      connected = false;
      currentPlayerId = null;
      connectPromise = null;
      resetDerivedState();

      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
      unsubscribers = [];
      await bridge.disconnect();
    },

    send: sendCommand,

    onEvent(handler: (event: RealtimeEvent) => void): () => void {
      listeners.add(handler);
      return () => {
        listeners.delete(handler);
      };
    },

    isConnected(): boolean {
      return connected;
    },
  };
}

export function createStdbRealtimeBackendFromBindings(
  config: StdbRealtimeBackendConfig,
): RealtimeBackend {
  return createStdbRealtimeBackend(createStdbBindingsBridge(), config);
}
